import {
    ApplicationRoleCode,
    CorporationJoinMode,
    IUserResponse,
    KcfHandling,
    LocationJoinMode,
} from '@kcftech/prometheustypescriptapiclient';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
    DataSourceSort,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { buildOrderBy, Filter, toDataSourceResult } from 'utils/query_v3';
import { Modify } from 'utils/types';

export const defaultUsersSort: DataSourceSort[] = [
    { field: 'FirstName', dir: 'asc' },
    { field: 'LastName', dir: 'asc' },
];

export type UsersFilter = Readonly<{
    corporationIds?: string[];
    locationIds?: string[];
    nameSearch?: string;
    applicationRoleCodes?: ApplicationRoleCode[];
    locationJoinMode?: LocationJoinMode;
    corporationJoinMode?: CorporationJoinMode;
    whereAdmin?: boolean;
    kcfHandling?: KcfHandling;
    isApproved?: boolean;
}>;

export type UsersDataSourceOptions = Modify<
    BaseDataSourceOptions<IUserResponse, UsersFilter>,
    { defaultUsersSort?: DataSourceSort[] }
>;
export type UsersPagedDataSource = PagedDataSource<IUserResponse, UsersFilter>;

const buildFilterExpression = ({
    applicationRoleCodes,
    isApproved,
}: Partial<UsersFilter>) =>
    Filter.and(
        applicationRoleCodes
            ? Filter.in('ApplicationRoleCode', applicationRoleCodes)
            : undefined,
        isApproved ? Filter.eq('IsApproved', isApproved) : undefined
    );

export const useKcfHandlingIfPermitted = (kcfHandling: KcfHandling | undefined) => {
    const { canViewKcfUsers } = usePermissions();

    return !canViewKcfUsers ? KcfHandling.Hide : kcfHandling ?? KcfHandling.Default;
};

const usersQuery: DataSourceQuery<IUserResponse, UsersFilter> = ({
    filter: {
        corporationIds,
        locationIds,
        nameSearch,
        applicationRoleCodes,
        locationJoinMode,
        corporationJoinMode,
        whereAdmin,
        kcfHandling,
        isApproved,
    },
    sort,
    page,
    pageLimit,
}) => {
    const filter = buildFilterExpression({
        applicationRoleCodes,
        isApproved,
    });
    const orderBy = buildOrderBy(sort, {
        descNullsLast: ['LastLogin', 'LastRequestTime'],
        ascNullsFirst: ['LastLogin', 'LastRequestTime'],
    });

    return toDataSourceResult(
        sdClient.getUsers({
            corporationIds,
            locationIds,
            corporationJoinMode: corporationJoinMode ?? CorporationJoinMode.ExplicitRole,
            locationJoinMode: locationJoinMode ?? LocationJoinMode.ExplicitRole,
            whereAdmin: whereAdmin ?? false,
            kcfHandling: kcfHandling ?? KcfHandling.Default,
            filter,
            page,
            pageLimit,
            orderBy,
            nameSearch,
        })
    );
};

export function usePagedUsersDataSource({
    defaultSort = defaultUsersSort,
    defaultFilter,
    ...options
}: UsersDataSourceOptions): UsersPagedDataSource {
    return usePagedDataSource<IUserResponse, UsersFilter>({
        defaultSort,
        ...options,
        defaultFilter: {
            ...defaultFilter,
            kcfHandling: useKcfHandlingIfPermitted(defaultFilter.kcfHandling),
        },
        query: usersQuery,
    });
}

export type UsersInfiniteDataSource = InfiniteDataSource<IUserResponse, UsersFilter>;

export function useInfiniteUsersDataSource({
    defaultSort = defaultUsersSort,
    defaultFilter,
    ...options
}: UsersDataSourceOptions): UsersInfiniteDataSource {
    return useInfiniteDataSource<IUserResponse, UsersFilter>({
        defaultSort,
        ...options,
        defaultFilter: {
            ...defaultFilter,
            kcfHandling: useKcfHandlingIfPermitted(defaultFilter.kcfHandling),
        },
        query: usersQuery,
    });
}
